/**
 * ProfileAssignmentTab - User-Profile Assignment Interface
 *
 * Allows admins to assign UI profiles to users.
 */

import React, { useState, useMemo } from 'react';
import { Search, UserCheck, AlertCircle, ChevronDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { uiProfileAPI } from '../../../api';

export function ProfileAssignmentTab({ profiles, users, onRefresh }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfile, setFilterProfile] = useState('all');
  const [assigning, setAssigning] = useState(null);

  // Filter users based on search and profile filter
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = searchTerm === '' ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter = filterProfile === 'all' ||
        (filterProfile === 'unassigned' && !user.ui_profile_id) ||
        user.ui_profile_id === parseInt(filterProfile);

      return matchesSearch && matchesFilter;
    });
  }, [users, searchTerm, filterProfile]);

  // Handle profile assignment
  const handleAssignProfile = async (userId, profileId) => {
    setAssigning(userId);
    try {
      await uiProfileAPI.assignToUser(userId, profileId);
      onRefresh();
    } catch (err) {
      console.error('Failed to assign profile:', err);
    } finally {
      setAssigning(null);
    }
  };

  // Get profile by ID
  const getProfile = (profileId) => profiles.find(p => p.id === profileId);

  // Calculate stats
  const stats = {
    total: users.length,
    assigned: users.filter(u => u.ui_profile_id).length,
    unassigned: users.filter(u => !u.ui_profile_id).length,
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          <div className="text-sm text-gray-500 dark:text-slate-400">{t('uiProfiles.assignment.stats.totalUsers')}</div>
        </div>
        <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20">
          <div className="text-2xl font-bold text-green-500 dark:text-green-400">{stats.assigned}</div>
          <div className="text-sm text-green-500/70 dark:text-green-400/70">{t('uiProfiles.assignment.stats.assigned')}</div>
        </div>
        <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
          <div className="text-2xl font-bold text-amber-500 dark:text-amber-400">{stats.unassigned}</div>
          <div className="text-sm text-amber-500/70 dark:text-amber-400/70">{t('uiProfiles.assignment.stats.unassigned')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400" size={18} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('uiProfiles.assignment.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={filterProfile}
          onChange={(e) => setFilterProfile(e.target.value)}
          className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">{t('uiProfiles.assignment.filterAll')}</option>
          <option value="unassigned">{t('uiProfiles.assignment.filterUnassigned')}</option>
          {profiles.map(profile => (
            <option key={profile.id} value={profile.id}>{profile.name}</option>
          ))}
        </select>
      </div>

      {/* Users table */}
      <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-700">
              <th className="text-left p-4 text-sm font-medium text-gray-500 dark:text-slate-400">{t('uiProfiles.assignment.columns.user')}</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500 dark:text-slate-400">{t('uiProfiles.assignment.columns.email')}</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500 dark:text-slate-400">{t('uiProfiles.assignment.columns.role')}</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500 dark:text-slate-400">{t('uiProfiles.assignment.columns.currentProfile')}</th>
              <th className="text-left p-4 text-sm font-medium text-gray-500 dark:text-slate-400">{t('uiProfiles.assignment.columns.action')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-gray-500 dark:text-slate-500">
                  {t('uiProfiles.assignment.noUsersFound')}
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => {
                const currentProfile = getProfile(user.ui_profile_id);
                return (
                  <tr key={user.id} className="border-b border-gray-100 dark:border-slate-700/50 hover:bg-gray-50 dark:hover:bg-slate-800/50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-white">
                          {user.first_name?.[0]}{user.last_name?.[0]}
                        </div>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {user.first_name} {user.last_name}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-gray-500 dark:text-slate-400">{user.email}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        user.role === 'admin' ? 'bg-purple-500/20 text-purple-500 dark:text-purple-400' :
                        user.role === 'manager' ? 'bg-blue-500/20 text-blue-500 dark:text-blue-400' :
                        'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-300'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="p-4">
                      {currentProfile ? (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: currentProfile.color }}
                          />
                          <span className="text-gray-900 dark:text-white">{currentProfile.name}</span>
                        </div>
                      ) : (
                        <span className="text-amber-500 dark:text-amber-400 flex items-center gap-1">
                          <AlertCircle size={14} />
                          {t('uiProfiles.assignment.notAssigned')}
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <ProfileDropdown
                        profiles={profiles.filter(p => p.status === 'active')}
                        currentProfileId={user.ui_profile_id}
                        disabled={assigning === user.id}
                        onChange={(profileId) => handleAssignProfile(user.id, profileId)}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Profile dropdown component
function ProfileDropdown({ profiles, currentProfileId, disabled, onChange }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="px-3 py-1.5 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 disabled:bg-gray-100 dark:disabled:bg-slate-800 text-gray-900 dark:text-white text-sm rounded-lg flex items-center gap-2 transition-colors"
      >
        <UserCheck size={14} />
        {t('uiProfiles.assignment.assignButton')}
        <ChevronDown size={14} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg shadow-xl z-20 overflow-hidden">
            {profiles.map(profile => (
              <button
                key={profile.id}
                onClick={() => {
                  onChange(profile.id);
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: profile.color }}
                  />
                  <span className="text-gray-900 dark:text-white text-sm">{profile.name}</span>
                </div>
                {currentProfileId === profile.id && (
                  <Check size={14} className="text-green-500 dark:text-green-400" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ProfileAssignmentTab;
